import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { contactsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Users, Plus, Search, Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePageTitle } from '../hooks/usePageTitle';

const ROLE_TAGS = ['client', 'venue', 'facilities', 'vendor', 'union', 'technical', 'freelancer', 'AVF Staff'];

const getRoleTagClass = (tag) => {
  switch (tag) {
    case 'client':
      return 'badge-client';
    case 'venue':
      return 'badge-venue';
    case 'vendor':
    case 'technical':
    case 'freelancer':
      return 'badge-vendor';
    case 'AVF Staff':
      return 'badge-partner';
    default:
      return 'border-zinc-500/30 bg-zinc-500/10 text-zinc-400';
  }
};

const Contacts = () => {
  usePageTitle('Contacts');
  const { isViewer } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [eventCounts, setEventCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pagination, setPagination] = useState({
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false
  });
  const [sortBy, setSortBy] = useState('contact_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const navigate = useNavigate();

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter]);

  useEffect(() => {
    loadContacts();
  }, [search, roleFilter, page, limit, sortBy, sortOrder]);

  useEffect(() => {
    loadEventCounts();
  }, []);

  const loadContacts = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        sort_by: sortBy,
        sort_order: sortOrder
      };
      if (search) params.search = search;
      if (roleFilter && roleFilter !== 'all') params.role_tag = roleFilter;
      
      const response = await contactsAPI.list(params);
      
      if (response.data.data && response.data.pagination) {
        setContacts(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setContacts(response.data);
        setPagination({
          total: response.data.length,
          total_pages: 1,
          has_next: false,
          has_prev: false
        });
      }
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };

  const loadEventCounts = async () => {
    try {
      const response = await contactsAPI.getEventCounts();
      const countsMap = {};
      response.data.forEach(item => {
        countsMap[item.contact_id] = item.event_count;
      });
      setEventCounts(countsMap);
    } catch (error) {
      console.error('Failed to load event counts:', error);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortOrder === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1" />
      : <ArrowDown className="h-4 w-4 ml-1" />;
  };

  const renderPagination = () => {
    const { total, total_pages, has_next, has_prev } = pagination;
    const startItem = (page - 1) * limit + 1;
    const endItem = Math.min(page * limit, total);

    const getPageNumbers = () => {
      const pages = [];
      const maxVisible = 5;
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(total_pages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    };

    return (
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-3 border-t border-border/50">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium text-foreground">{startItem}</span> to{' '}
          <span className="font-medium text-foreground">{endItem}</span> of{' '}
          <span className="font-medium text-foreground">{total}</span> contacts
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Rows:</span>
            <Select value={limit.toString()} onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}>
              <SelectTrigger className="w-16 h-8 bg-background/50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(1)} disabled={!has_prev}>
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page - 1)} disabled={!has_prev}>
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex items-center gap-1">
              {getPageNumbers().map((pageNum) => (
                <Button
                  key={pageNum}
                  variant={pageNum === page ? "default" : "outline"}
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(pageNum)}
                >
                  {pageNum}
                </Button>
              ))}
            </div>

            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(page + 1)} disabled={!has_next}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setPage(total_pages)} disabled={!has_next}>
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight flex items-center gap-3">
            <Users className="h-8 w-8 text-cyan-400" />
            Contacts
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage contacts for clients, venues, vendors, and your team
          </p>
        </div>
        {!isViewer && (
          <Button asChild data-testid="add-contact-btn">
            <Link to="/contacts/new">
              <Plus className="h-4 w-4 mr-2" />
              Add Contact
            </Link>
          </Button>
        )}
      </div>

      {/* Filters */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search contacts..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 bg-background/50"
                data-testid="search-contacts"
              />
            </div>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-background/50" data-testid="role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {ROLE_TAGS.map((tag) => (
                  <SelectItem key={tag} value={tag}>
                    {tag}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-primary"></div>
            </div>
          ) : contacts.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('contact_name')}
                    >
                      <div className="flex items-center">
                        Name
                        {getSortIcon('contact_name')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('organization_name')}
                    >
                      <div className="flex items-center">
                        Organization
                        {getSortIcon('organization_name')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="data-table cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSort('email')}
                    >
                      <div className="flex items-center">
                        Email
                        {getSortIcon('email')}
                      </div>
                    </TableHead>
                    <TableHead className="data-table">Events</TableHead>
                    <TableHead className="data-table">Roles</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow
                      key={contact.id}
                      className="border-border/50 hover:bg-muted/30 cursor-pointer"
                      onClick={() => navigate(`/contacts/${contact.id}`)}
                      data-testid={`contact-row-${contact.id}`}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium">{contact.contact_name}</p>
                          {contact.title && (
                            <p className="text-sm text-muted-foreground">{contact.title}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.organization_name || '-'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.email || '-'}
                      </TableCell>
                      <TableCell>
                        {eventCounts[contact.id] > 0 ? (
                          <Badge 
                            variant="outline" 
                            className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
                            data-testid={`event-count-${contact.id}`}
                          >
                            <Calendar className="h-3 w-3 mr-1" />
                            {eventCounts[contact.id]}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {contact.role_tags.slice(0, 2).map((tag) => (
                            <Badge
                              key={tag}
                              variant="outline"
                              className={`text-xs ${getRoleTagClass(tag)}`}
                            >
                              {tag}
                            </Badge>
                          ))}
                          {contact.role_tags.length > 2 && (
                            <Badge variant="outline" className="text-xs">
                              +{contact.role_tags.length - 2}
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {renderPagination()}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No contacts found</p>
              <p className="text-sm mt-1">Add your first contact to get started</p>
              <Button asChild className="mt-4" variant="outline">
                <Link to="/contacts/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Contact
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Contacts;
